/**
 * Notification Context
 * Manages application-wide notifications
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
} from "react";
import type {
  Notification,
  NotificationContextState,
} from "@/types/notification";

const NotificationContext = createContext<NotificationContextState | null>(
  null,
);

/**
 * Mock notifications for development
 */
const MOCK_NOTIFICATIONS: Notification[] = [
  {
    id: "1",
    type: "service_requested",
    priority: "high",
    title: "Nueva solicitud de servicio",
    message: "Hotel Meridian ha solicitado un servicio de desratización",
    timestamp: new Date(Date.now() - 1000 * 60 * 5), // 5 minutos atrás
    read: false,
    actionUrl: "/workorders",
    metadata: {
      clientId: "hotel-meridian",
    },
  },
  {
    id: "2",
    type: "low_stock",
    priority: "high",
    title: "Stock bajo: Rodenticida",
    message: "El producto Rodenticida tiene solo 5 unidades disponibles",
    timestamp: new Date(Date.now() - 1000 * 60 * 30), // 30 minutos atrás
    read: false,
    actionUrl: "/inventory",
    metadata: {
      productId: "rodenticida-pro",
      stockLevel: 5,
    },
  },
  {
    id: "3",
    type: "service_started",
    priority: "medium",
    title: "Servicio iniciado",
    message: "María García ha iniciado el servicio OT-2026-002",
    timestamp: new Date(Date.now() - 1000 * 60 * 60), // 1 hora atrás
    read: false,
    actionUrl: "/workorders",
    metadata: {
      workOrderId: "OT-2026-002",
    },
  },
  {
    id: "4",
    type: "low_stock",
    priority: "medium",
    title: "Stock bajo: Gel Insecticida",
    message: "El producto Gel Insecticida tiene solo 8 unidades disponibles",
    timestamp: new Date(Date.now() - 1000 * 60 * 120), // 2 horas atrás
    read: true,
    actionUrl: "/inventory",
    metadata: {
      productId: "gel-insecticida",
      stockLevel: 8,
    },
  },
  {
    id: "5",
    type: "service_completed",
    priority: "low",
    title: "Servicio completado",
    message: "Juan Pérez ha completado el servicio OT-2026-001",
    timestamp: new Date(Date.now() - 1000 * 60 * 180), // 3 horas atrás
    read: true,
    actionUrl: "/workorders",
    metadata: {
      workOrderId: "OT-2026-001",
    },
  },
];

/**
 * Notification Provider Props
 */
interface NotificationProviderProps {
  children: ReactNode;
}

/**
 * Notification Provider Component
 */
export function NotificationProvider({
  children,
}: NotificationProviderProps): React.ReactElement {
  const [notifications, setNotifications] =
    useState<Notification[]>(MOCK_NOTIFICATIONS);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const addNotification = useCallback(
    (notification: Omit<Notification, "id" | "timestamp" | "read">) => {
      const newNotification: Notification = {
        ...notification,
        id: `notif-${Date.now()}-${Math.random()}`,
        timestamp: new Date(),
        read: false,
      };

      setNotifications((prev) => [newNotification, ...prev]);
    },
    [],
  );

  const markAsRead = useCallback((id: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n)),
    );
  }, []);

  const markAllAsRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  }, []);

  const deleteNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  const value: NotificationContextState = {
    notifications,
    unreadCount,
    addNotification,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAll,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

/**
 * Hook to use notification context
 */
export function useNotifications(): NotificationContextState {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotifications must be used within NotificationProvider",
    );
  }
  return context;
}
